import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { AuthContext } from './auth-context'
import PropTypes from 'prop-types'
import { loginRequest, msalInstance } from './msal'
import { appInsights } from 'src/AppInsights'
import { clearAllStorages, setTokensInLocalStorage } from 'src/API/Actions'
import { useDispatch, useSelector } from 'react-redux'
import { setInitialize } from 'src/redux/slices/login.slice'
import { checkUser, getDefaultRoutes } from 'src/redux/slices/Login/login.async'
import { useLocation, useNavigate } from 'react-router-dom'
import { useIsAuthenticated, useMsal } from '@azure/msal-react'
import { setAccessDenied } from 'src/redux/slices/route.slice'

const AuthProvider = ({ children }) => {
  const authenticated = useIsAuthenticated()
  const { accounts } = useMsal()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const [loginLoader, setLoginLoader] = useState(false)
  const { allRouteList } = useSelector((state) => state.routeData)

  const { routeToNavigate } = useSelector((state) => state.login)

  const { fetchingUserLoader, isLoggedIn, defaultRoute, fetchingDefRouteLoader, initialize } =
    useSelector((state) => state.login)

  const loginHandler = useCallback(async () => {
    try {
      clearAllStorages()
      setLoginLoader(true)
      await msalInstance.initialize()
      await msalInstance.loginRedirect(loginRequest)
    } catch (error) {
      setLoginLoader(false)
      dispatch(setInitialize(false))
    }
  }, [])

  useEffect(() => {
    if (isLoggedIn && defaultRoute && authenticated) {
      if (routeToNavigate) {
        navigate(routeToNavigate)
      } else {
        navigate(defaultRoute)
      }
      dispatch(setInitialize(false))
    }
  }, [isLoggedIn, defaultRoute])

  const isAuthorized = (list, path) => {
    return list.find((item) => path.includes(item))
  }

  useEffect(() => {
    if (isLoggedIn && location) {
      const routList = allRouteList.map((item) => item.config_path)
      const isAuthorizedRoute = isAuthorized(routList, location.pathname)
      if (location.key === 'default' && !isAuthorizedRoute) {
        dispatch(setAccessDenied(true))
      } else {
        dispatch(setAccessDenied(false))
      }
    }
  }, [location, isLoggedIn])

  const initializeMsal = async () => {
    try {
      if (accounts.length > 0 && !isLoggedIn && !defaultRoute) {
        dispatch(setInitialize(true))
        if (!appInsights?.core?.isInitialized()) {
          appInsights?.clearAuthenticatedUserContext()
          appInsights.getCookieMgr().set('ai_user', accounts[0]?.username)
          appInsights.getCookieMgr().set('ai_session', accounts[0]?.name)
          appInsights.loadAppInsights()
        }
        msalInstance.setActiveAccount(accounts[0])
        await setTokensInLocalStorage()
        const token = accounts[0]?.idToken
        if (token) {
          dispatch(checkUser({ token })).then((res) => {
            if (res.meta.requestStatus === 'fulfilled') {
              dispatch(getDefaultRoutes())
            }
          })
          setLoginLoader(false)
        }
      }
    } catch (error) {
      console.error(error)
    }
  }
  useEffect(() => {
    initializeMsal()
  }, [accounts])

  const emptyCache = async () => {
    if ('caches' in window) {
      caches.keys().then((names) => {
        names.forEach((name) => {
          caches.delete(name)
        })
      })
    }
  }
  const logOutHandler = useCallback(async () => {
    try {
      await emptyCache()
      if (appInsights?.core?.isInitialized()) {
        appInsights.unload()
      }
      localStorage.removeItem('allowed_domain')
      sessionStorage.removeItem('persist:root')
      await msalInstance.logoutRedirect({ account: accounts[0] })
      navigate('/')
    } catch (error) {
      console.error(error)
    }
  }, [])

  const memoizedValue = useMemo(
    () => ({
      loginLoader: initialize || loginLoader,
      loading: initialize,
      logOutHandler,
      loginHandler,
      setLoginLoader,
    }),
    [
      loginLoader,
      logOutHandler,
      loginHandler,
      setLoginLoader,
      fetchingUserLoader,
      fetchingDefRouteLoader,
      initialize,
    ],
  )

  return <AuthContext.Provider value={memoizedValue}>{children}</AuthContext.Provider>
}

export default AuthProvider

AuthProvider.propTypes = {
  children: PropTypes.node,
}
